import React, { useState, useEffect, useRef } from "react";
import useFilters from "hooks/useFilters";
import { useHistory } from "react-router-dom";
import { useStore } from "store/context";
import i18n from "i18n";
import { isMobile } from "helpers/Mobile";

import { BusinessService } from "services/BusinessService";
import { PublicService } from "services/PublicService";

import styles from "./Businesses.module.scss";
import AdminLayout from "components/MainApp/layouts/DesktopLayout";
import CompanyTable from "./components/BusinessTable";

import Search from "components/MainApp/atoms/Filters/Search";
import FilterPanel from "components/MainApp/organisms/Filters/FilterPanel";
import headers from "./headers";
import Pagination from "components/MainApp/atoms/Pagination";
import Loader from "components/MainApp/atoms/Loader";

import { filterDataSource, filterTypeMap } from "./data/filterData";

import MainNotification from "components/MainApp/atoms/Notification";
import SimpleSearchPanel from "components/MainApp/organisms/Filters/SimpleSearchPanel";
import { NotFoundItems } from "../components/NotFoundItems";
import { AuthService } from "services/AuthService";

const initialFilters = {
  page_size: 10,
  search: "",
  ordering: "-created_at",
  enabled: undefined
};

export const BusinessesView = () => {
  const [companyFilters, setCompanyFilters] = useState({});
  const history = useHistory();
  const [loading, setLoading] = useState(false);
  const [noCompanies, setNoCompanies] = useState(false);
  const [filterData, setFilterData] = useState(filterDataSource);

  const [companies_name, setCompanies_name] = useState([]);
  const [submitted, setSubmitted] = useState(false);
  const [isFirstLoad, setIsFirstLoad] = useState(true);

  let scrolling = false;

  const headerOptions = [
    {
      icon: "icon-iconos_mas2",
      name: "add",
      handler: () => {
        history.push("/admin/businesses/add");
      },
      tooltip: i18n.t("add.company")
    }
  ];

  const [
    filters,
    setFilters,
    selectedFiltersAll,
    selectedFiltersByType,
    handleApplyFilters,
    handleSearch,
    headerOrderingHandler,
    resetOrdering,
    handleFiltersChangeOrdering,
    applied,
    page,
    setPage,
    items,
    setItems,
    pager,
    setPager,
    header,
    setHeader,
    mobilePanelOpen,
    setMobilePanelOpen
  ] = useFilters(
    initialFilters,
    companyFilters,
    filterTypeMap,
    isMobile,
    headers
  );

  useEffect(() => {
    fillFilterData();
  }, []);

  useEffect(() => {
    const first_load =
      JSON.stringify(initialFilters) === JSON.stringify(filters);

    filter(first_load);
    // Acomodar orden
    if (filters.ordering) {
      const keyOrdering =
        filters.ordering.charAt(0) === "-"
          ? filters.ordering.substr(1)
          : filters.ordering;
      if (keyOrdering in headers) {
        const newHeaders = { ...header };
        newHeaders[keyOrdering]["active"] = true;
        newHeaders[keyOrdering]["direction"] =
          filters.ordering === keyOrdering ? true : false;
        setHeader(newHeaders);
      }
    }
  }, [filters]);

  useEffect(() => {
    if (isFirstLoad) {
      setIsFirstLoad(false);
      return;
    }
    filter(false);
    setCompanyFilters({
      filters: filters,
      selectedFiltersAll: selectedFiltersAll,
      selectedFiltersByType: selectedFiltersByType
    });
  }, [filters, page]);

  useEffect(() => {
    window.addEventListener("scroll", handleScroll, true);

    return () => {
      window.removeEventListener("scroll", handleScroll, true);
    };
  });

  const goBack = () => {
    history.push("/admin/more-options/1");
  };

  const goToCompanyDetailsPage = id => {
    history.push(`/admin/businesses/edit/${id}`);
  };

  const handleScroll = event => {
    if (!isMobile) return;

    if (
      event.srcElement.offsetHeight + event.target.scrollTop + 100 <
      event.srcElement.scrollHeight
    )
      return;

    if (!pager.next || submitted || scrolling) return;
    scrolling = true;

    setPage(page + 1);
  };

  const handlePagerChange = function (page) {
    setPage(page);
  };

  const filter = function (firstLoad = false) {
    let append = isMobile;
    if (!append) {
      setItems([]);
    }

    //BEFORE SUBMIT
    document.body.style.cursor = "wait";
    setSubmitted(true);

    const data = { ...filters, page: page };

    BusinessService.businesses(data)
      .then(response => {
        setSubmitted(false);
        document.body.style.cursor = "default";
        scrolling = false;

        if (firstLoad && response.count === 0) {
          // history.push("/admin/businesses/load");
          setNoCompanies(true);
          return;
        }

        const newItems = append
          ? [...items, ...response.results]
          : response.results;

        setLoading(false);
        setItems(newItems);
        setPager({
          next: response.next,
          previous: response.previous,
          count: response.count,
          countPages: Math.ceil(response.count / filters.page_size)
        });
      })
      .catch(e => {
        setSubmitted(false);
        document.body.style.cursor = "default";
        scrolling = false;
      });
  };

  const fillFilterData = function () {
    let filterDataAux = [...filterData];
    filterDataAux[0].options = filterDataAux[0].options.map(option => {
      option.selected = false;
      return option;
    });

    if (isMobile) {
      filterDataAux[0] = {
        type: "twoOption",
        options: [
          {
            selected: false,
            label: i18n.t("less.recent"),
            value: "created_at"
          }
        ],
        label: i18n.t("order.by"),
        firstOptionLabel: i18n.t("latest"),
        value: "-created_at"
      };
    }
    setFilterData(filterDataAux);
  };

  const desktopContent = (
    <div>
      <SimpleSearchPanel
        placeholder={i18n.t("search")}
        handleSubmit={handleSearch}
      />

      {items.length > 0 && (
        <div className={styles["business-table-table"]}>
          <div className={styles["business-table-top-table"]}>
            <div className={styles["business-table-top-total"]}>
              {i18n.t("total").toUpperCase()}{" "}
              <span className={styles["business-table-top-total-bold"]}>
                ({pager.count})
              </span>
            </div>
            <div className={styles["business-table-top-sort"]}>
              <div
                data-automation-id="business-table-sort"
                className={
                  filters?.ordering?.includes("created_at")
                    ? `${styles["business-table-top-sort"]} ${styles["business-table-active-filter"]}`
                    : styles["business-table-top-sort"]
                }
                onClick={() => {
                  handleFiltersChangeOrdering(
                    "ordering",
                    filters.ordering === "-created_at"
                      ? "created_at"
                      : "-created_at"
                  );
                  resetOrdering();
                }}
              >
                {filters.ordering === "created_at" ? (
                  <React.Fragment>
                    {i18n.t("less.recent")}{" "}
                    <i
                      className={`icon-chevron ${styles["business-table-top-icon"]}`}
                    ></i>
                  </React.Fragment>
                ) : (
                  <React.Fragment>
                    {" "}
                    {i18n.t("latest")}{" "}
                    <i
                      className={`icon-chevron-down ${styles["business-table-chevron-down"]} ${styles["business-table-top-icon"]}`}
                    ></i>
                  </React.Fragment>
                )}
              </div>
            </div>
          </div>
          <CompanyTable
            businesses={items}
            headers={header}
            headerClickHandler={headerOrderingHandler}
            handleClick={goToCompanyDetailsPage}
          />
        </div>
      )}

      {items.length === 0 && !submitted && <NotFoundItems />}
      {items.length > 0 && (
        <Pagination
          pageQuantity={pager.countPages}
          currentPage={page}
          pageChangeHandler={handlePagerChange}
        />
      )}
    </div>
  );

  const mobileContent = (
    <>
      <SimpleSearchPanel placeholder={i18n.t("search")} handleSubmit={handleSearch} />

      {items.length > 0 && (
        <div className={styles["business-table-table"]}>
          <div className={styles["business-table-top-table"]}>
            <div className={styles["business-table-top-total"]}>
              {i18n.t("total").toUpperCase()}{" "}
              <span className={styles["business-table-top-total-bold"]}>
                ({pager.count})
              </span>
            </div>
            <div className={styles["business-table-top-sort"]}>
              <div
                data-automation-id="business-table-sort"
                className={
                  filters?.ordering?.includes("created_at")
                    ? "business-table-top-sort business-table-active-filter"
                    : "business-table-top-sort"
                }
                onClick={() => {
                  handleFiltersChangeOrdering(
                    "ordering",
                    filters.ordering === "-created_at"
                      ? "created_at"
                      : "-created_at"
                  );
                  resetOrdering();
                }}
              >
                {filters.ordering === "created_at" ? (
                  <React.Fragment>
                    {i18n.t("less.recent")}{" "}
                    <i className={`icon-chevron ${styles["business-table-top-icon"]}`}></i>
                  </React.Fragment>
                ) : (
                  <React.Fragment>
                    {" "}
                    {i18n.t("latest")}{" "}
                    <i className={`icon-chevron-down ${styles["business-table-chevron-down"]} ${styles["business-table-top-icon"]}`}></i>
                  </React.Fragment>
                )}
              </div>
            </div>
          </div>
          <CompanyTable
            businesses={items}
            filtersApplied={filters.search.length !== 0}
            headers={header}
            headerClickHandler={headerOrderingHandler}
            handleClick={goToCompanyDetailsPage}
          />
        </div>
      )}

      {items.length === 0 && !submitted && filters.search.length !== 0 && (
        <NotFoundItems />
      )}
    </>
  );

  const tableContent = isMobile ? mobileContent : desktopContent;

  const noproductcontent = (
    <div className={styles["business-table-no-business-container"]}>
      <p className={styles["business-table-no-business-title"]}>
        {i18n.t("no.business.created")}
      </p>
      <p className={styles["business-table-no-business-subtitle"]}>
        {i18n.t("create.for.first.business")}
      </p>

      <div
        className={styles["business-table-no-business-button"]}
        onClick={() => history.push("/admin/businesses/add")}
      >
        {i18n.t("create").toUpperCase()}
      </div>
    </div>
  );

  const companyContent = noCompanies ? noproductcontent : tableContent;

  const content = (
    <div className={styles["business-content-container"]}>
      {loading ? <Loader /> : companyContent}
    </div>
  );

  return (
    <div className={styles["business-container"]}>
      <AdminLayout
        headerTitle={i18n.t("companies")}
        headerOptions={headerOptions}
        content={content}
        goBackFunc={goBack}
        navHidden={false}
      ></AdminLayout>
      <MainNotification
        label={i18n.t("product.updated.successfully.message")}
        open={false}
      ></MainNotification>
    </div>
  );
};

export default BusinessesView;
